/**
 * Fetch a Turnstile-protected endpoint with a one-shot retry on verification failure.
 *
 * Why: PostHog showed "Verification failed" as 27% of all transcript errors (100 hits / 56
 * users in 30d), skewed to mobile, and the page treated it as a dead end ("refresh and try
 * again"). 37% of those users never got a transcript. The common causes are transient — the
 * Cloudflare script had not finished loading when the user submitted, or the token had
 * expired / been consumed — and a FRESH token fixes them. So on a `turnstile_failed` 403 we
 * obtain a new token and retry exactly once. Anything else is returned untouched.
 *
 * Pure with respect to its dependencies (token getter + fetch are injected) so the retry
 * decision is unit-tested without a browser.
 */

export interface TurnstileFetchResult<T = unknown> {
  response: Response;
  data: T;
  /** True when the first attempt failed Turnstile and a second attempt was made. */
  retried: boolean;
}

/** A 403 whose body carries `turnstile_failed: true` — the server's verification rejection. */
export function isTurnstileFailure(status: number, body: unknown): boolean {
  if (status !== 403 || !body || typeof body !== 'object') return false;
  return (body as { turnstile_failed?: unknown }).turnstile_failed === true;
}

// Default T mirrors `response.json()` (Promise<any>) so call sites index `data` as before.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchWithTurnstile<T = any>(
  input: string,
  init: RequestInit,
  getToken: () => Promise<string>,
  doFetch: typeof fetch = fetch,
): Promise<TurnstileFetchResult<T>> {
  const attempt = async () => {
    const token = await getToken();
    const headers: Record<string, string> = {
      ...((init.headers as Record<string, string> | undefined) ?? {}),
      ...(token ? { 'cf-turnstile-token': token } : {}),
    };
    const response = await doFetch(input, { ...init, headers });
    const data = (await response.json().catch(() => ({}))) as T;
    return { response, data };
  };

  let { response, data } = await attempt();
  let retried = false;
  if (isTurnstileFailure(response.status, data)) {
    retried = true;
    ({ response, data } = await attempt());
  }
  return { response, data, retried };
}
